// İSG uzmanı portalı — Panel "Aksiyon gerekli" listesi (S34-4, api-contract §34.2, design review
// `S34-carbon-phase2-plan.md` §4 "Aksiyon gerekli" row). `GET /dashboard/action-items` — sayfalı,
// `kind` süzgeçli. Süzgeç çipleri `summary.actionItemCounts`ten (§34.1'de taşınır, BU uçta
// DEĞİL — M11'in çözümü zaten backend'de vardı, `countsByKind` İCAT EDİLMEDİ).
//
// **Satır kopyası kısmen özgündür (bulgulanmış bir sözleşme çelişkisi).** Copy sheet'in
// `dashboard.actions.row.pendingApproval` şablonu ("{{permit}} — {{contractor}} — {{date}}
// tarihinde GÖNDERİLDİ") bir gönderim tarihi varsayar; §34.2'nin normatif tablosu `dueAt`'ın
// `pendingApproval` için iznin **`validityStart`'ı** olduğunu söyler — bir gönderim anı DEĞİL.
// Üç `row.*` şablonu (dosya başlığındaki aynı sebeple `permitExpiring` da) bu yüzden
// KULLANILMADI (yanlış alanı "gönderildi" diye ETİKETLERdi); satırlar bunun yerine ayrı
// parçalardan kurulur: satır 1 (`entryBlocked` için `dashboard.actions.line.personReason`,
// diğer ikisinde salt `permitTitle`) + satır 2 (taşeron/bağlam) + sağ sütunda `dueAt`'tan
// türetilen GÖRECELİ bir süre (`dashboard.actions.due.*`) — tasarım incelemesinin "Right column:
// relative due date" maddesiyle birebir. Kalan copy (`dashboard.actions.reason.*`,
// `dashboard.actions.kind.*`, `dashboard.actions.empty*`) VERBATIM kullanılır.
//
// **S34-45 design review M2 — `dueLabel` artık `kind`e göre DALLANIR.** Üç `kind` de `dueAt`'ı
// aynı GENEL "geçti/kaldı" metniyle ("Süresi geçti") gösteriyordu; `entryBlocked` için `dueAt`
// iznin `validityStart`'ıdır, yani geçmiş bir tarih "izin BAŞLADI, kişi HÂLÂ giremiyor" demektir —
// "Eksik belge: Sağlık raporu" satırının yanında kırmızı "Süresi geçti" bunu "BELGENİN süresi
// doldu" diye YANLIŞ okutuyordu (çoğu engelli satırda bu okunuyordu). Kopya artık `kind`e göre:
// `entryBlocked` (geçmiş → "İzin başladı", gelecek → "{n} gün sonra başlıyor"),
// `expiringPermit` ("{n} gün içinde bitiyor" / "Bugün bitiyor", geçmiş hâlâ "Süresi geçti" —
// burada DOĞRU okur, izin GERÇEKTEN süresi dolmuş demektir), `pendingApproval` (geçmiş →
// "Başlangıç tarihi geçti", gelecek → "{n} gün sonra başlıyor" — `entryBlocked`le AYNI alan,
// AYNI gelecek metni). Kırmızı (`overdue: true`) yalnız GEÇMİŞ durumlarda kalır.
//
// **M3 — süzülmüş boş durum "hiç aksiyon yok" demez.** `kind !== 'all'` iken ve "Tümü" rozeti
// 0'dan büyükken artık "Bu türde bekleyen aksiyon yok." + "Tümünü göster" (kind'ı sıfırlayan
// ghost aksiyon) gösterilir, `role="status"` ile duyurulur — eskiden HER boş durumda "Şu anda
// aksiyon gerektiren bir durum yok." basılıyordu, bu bir süzgeç seçiliyken YANLIŞTI (§34.1
// `actionItemCounts` zaten 0 olmayan bir sayı gösteriyor OLABİLİRDİ). İkisi de artık
// `EmptyState variant="panel"` (ikonsuz, tasarım incelemesi) — eski `table` variant + 32px
// `CircleCheck` ikonu bir dashboard panelinde fazla ağırdı.
//
// **owner review B3-2 — satır artık TÜM engel sebeplerini gösterir.** `reasonFragment` eskiden
// `item.reason`e (yalnız BİRİNCİL sebep, §34.2) göre TEK bir diziyi (`missingDocumentTypes` YA
// DA `missingTrainingTitles`) basıyordu — bir kişinin hem eksik belgesi HEM eksik eğitimi varsa
// ikincisi SESSİZCE düşüyordu. `blockingReasonFragments` (`lib/blockingReasons.ts`) ikisini de
// okur; aynı yardımcı izin detayının Personel görünümünde de kullanılır (`PersonnelDrawer.tsx`)
// — iki yüzey AYNI gerçeği AYNI kelimelerle gösterir (§35.3 "İki yüzey aynı gerçeği gösterir").

import {
  Alert,
  Button,
  EmptyState,
  type ExpertActionItemCountsDto,
  type ExpertActionItemDto,
  type ExpertActionItemKind,
  Pagination,
  Skeleton,
  currentCulture,
  dashboardApi,
  resolveApiErrorMessage,
  useDocumentTypes,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ChevronRight, ClipboardCheck, UserX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { blockingReasonFragments } from '../../lib/blockingReasons';
import { dashboardKeys } from '../../lib/dashboardKeys';
import type { DocumentTypeLabel } from '../../lib/eligibility';
import { calendarDayDiff, formatDate } from '../../lib/format';
import { FilterChipGroup, type FilterChipOption } from '../FilterChipGroup';
import { PanelHeader } from '../PanelHeader';

const PAGE_SIZE = 8;

type KindFilter = ExpertActionItemKind | 'all';

const KIND_ICONS: Record<ExpertActionItemKind, typeof UserX> = {
  entryBlocked: UserX,
  expiringPermit: CalendarClock,
  pendingApproval: ClipboardCheck,
};

const KIND_LABEL_KEYS: Record<ExpertActionItemKind, string> = {
  entryBlocked: 'dashboard.actions.kind.entryBlocked',
  // Copy sheet'in ters sözcük sırası ("Süresi dolacak izin") — wire `kind` değeriyle DOĞRUDAN
  // birleştirilemez (`dashboard.actions.kind.${kind}` üretimi bu TEK kind'ı KAÇIRIRDI); bu
  // yüzden açık bir eşleme tablosu.
  expiringPermit: 'dashboard.actions.kind.permitExpiring',
  pendingApproval: 'dashboard.actions.kind.pendingApproval',
};

/** owner review B3-2 — HER İKİ eksik türünü de gösterir (bkz. dosya başlığı notu), yalnız
 * birincil `item.reason`i DEĞİL. `·` ile ayrılır (bu dosyanın satır-2 ayracıyla AYNI karakter). */
function reasonFragment(
  item: ExpertActionItemDto,
  t: (key: string, options?: Record<string, unknown>) => string,
  label: DocumentTypeLabel,
): string {
  return blockingReasonFragments(
    item.missingDocumentTypes ?? [],
    item.missingTrainingTitles ?? [],
    item.reason === 'missingTraining',
    t,
    label,
  ).join(' · ');
}

/** `dueAt`ten `kind`e göre DALLANAN göreceli bir etiket (M2, dosya başlığı) — `expiringPermit`
 * için `dueAt` iznin bitişi, diğer ikisi için başlangıcıdır; aynı "geçmiş/bugün/gelecek" iskeleti
 * her `kind` için FARKLI bir fiil taşır (dosya başlığındaki tablo). */
function dueLabel(
  item: ExpertActionItemDto,
  t: (key: string, options?: Record<string, unknown>) => string,
  culture: string,
): { text: string; overdue: boolean } {
  // Ham ms farkı DEĞİL, YEREL takvim günü farkı (bkz. `calendarDayDiff` — saat 23:50'de yarın
  // sabah başlayan bir izin artık "bugün başlıyor" YANLIŞINI vermez).
  const days = calendarDayDiff(item.dueAt);

  if (item.kind === 'expiringPermit') {
    if (days < 0) {
      return { text: t('dashboard.actions.due.overdue'), overdue: true };
    }
    if (days === 0) {
      return { text: t('dashboard.actions.due.endsToday'), overdue: false };
    }
    if (days === 1) {
      return { text: t('dashboard.actions.due.endsTomorrow'), overdue: false };
    }
    return {
      text: t('dashboard.actions.due.endsOnDate', { date: formatDate(item.dueAt, culture) }),
      overdue: false,
    };
  }

  // `entryBlocked` ve `pendingApproval` — ikisi de `dueAt = validityStart` (§34.2 normatif tablo).
  if (days < 0) {
    const key =
      item.kind === 'entryBlocked'
        ? 'dashboard.actions.due.permitStarted'
        : 'dashboard.actions.due.startDatePassed';
    return { text: t(key), overdue: true };
  }
  if (days === 0) {
    return { text: t('dashboard.actions.due.startsToday'), overdue: false };
  }
  if (days === 1) {
    return { text: t('dashboard.actions.due.startsTomorrow'), overdue: false };
  }
  return {
    text: t('dashboard.actions.due.startsOnDate', { date: formatDate(item.dueAt, culture) }),
    overdue: false,
  };
}

function ActionItemRow({ item }: { item: ExpertActionItemDto }) {
  const { t } = useTranslation();
  const { label } = useDocumentTypes();
  const Icon = KIND_ICONS[item.kind];
  const line1 =
    item.kind === 'entryBlocked'
      ? t('dashboard.actions.line.personReason', {
          person: item.personnelName,
          reason: reasonFragment(item, t, label),
        })
      : item.permitTitle;
  const line2 = item.kind === 'entryBlocked' ? item.permitTitle : item.contractorName;
  const due = dueLabel(item, t, currentCulture());

  return (
    <li className="border-b border-border last:border-b-0">
      <Link
        to={`/permits/${item.permitUuid}`}
        className="flex min-h-14 items-center gap-3 px-4 py-2 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <Icon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-fg" title={line1}>
            {line1}
          </p>
          <p className="truncate text-xs text-muted" title={line2}>
            {line2}
          </p>
        </div>
        <span
          className={`tnum shrink-0 text-xs ${due.overdue ? 'font-medium text-danger-strong' : 'text-muted'}`}
        >
          {due.text}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
      </Link>
    </li>
  );
}

function LoadingRows() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 4 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: sabit sayıda dekoratif hayalet satır.
        <div key={i} className="flex min-h-14 items-center gap-3 border-b border-border px-4 py-2">
          <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-2.5 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface ActionItemsPanelProps {
  /** `summary.actionItemCounts` — parent'ın TEK `GET /dashboard/summary` isteğinden (M11'in
   * çözümü orada yaşar). Henüz yüklenmediyse `null` — çipler geçici olarak 0 gösterir. */
  counts: ExpertActionItemCountsDto | null;
  /** `?kind=` ile gelen bir panonun KPI karosu (personel eksik belge) — yalnız İLK render'da
   * uygulanır (bkz. `DashboardPage.tsx`). */
  initialKind?: ExpertActionItemKind;
  /** Panonun saha seçicisi (§35.2, owner review B3-1) — `null` = tüm sahalar. Parent'ın
   * `activeSite`'ıyla AYNI kaynak (`DashboardPage.tsx`); bu panel kendi seçici GÖSTERMEZ. */
  siteUuid: string | null;
}

export function ActionItemsPanel({ counts, initialKind, siteUuid }: ActionItemsPanelProps) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<KindFilter>(initialKind ?? 'all');
  const [page, setPage] = useState(1);

  // KPI karosu panel ZATEN MONTELİYKEN farklı bir `kind`e işaret ederse (aynı sayfa içi
  // gezinme) filtreyi GÜNCELLER — `useSiteContext`'in "bir tık sonra yerine oturur" deseniyle
  // AYNI, render SIRASINDA dış durumu DEĞİŞTİRMEZ.
  useEffect(() => {
    if (initialKind) {
      setKind(initialKind);
      setPage(1);
    }
    // Yalnız KPI karosunun linki DEĞİŞTİĞİNDE (yeni bir tıklama) — `kind`in KENDİSİ bu efektin
    // bağımlılığı DEĞİL, aksi hâlde kullanıcının elle seçtiği çipi HER render'da EZERdi. `setKind`/
    // `setPage` state setter'ları (stabil kimlik) — biome bu yüzden zaten bağımlılık İSTEMEDİ.
  }, [initialKind]);

  // Saha değişince (parent'ın seçicisi) sayfa 1'e döner — owner review B3-1: aksi hâlde eski
  // sahanın 3. sayfasında kalıp yeni sahanın (daha kısa olabilecek) listesinde boş bir sayfa
  // görünürdü (`Pagination`'ın kendi "aralık dışı" davranışı — bkz. o dosyanın notu).
  // biome-ignore lint/correctness/useExhaustiveDependencies: kasıtlı — yalnız `siteUuid` değişince tetiklemeli
  useEffect(() => {
    setPage(1);
  }, [siteUuid]);

  const query = useQuery({
    queryKey: dashboardKeys.actionItems({
      kind: kind === 'all' ? undefined : kind,
      siteUuid: siteUuid ?? undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    queryFn: () =>
      dashboardApi.listDashboardActionItems({
        kind: kind === 'all' ? undefined : kind,
        siteUuid: siteUuid ?? undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });

  const safeCounts = counts ?? { entryBlocked: 0, expiringPermit: 0, pendingApproval: 0 };
  const totalAll = safeCounts.entryBlocked + safeCounts.expiringPermit + safeCounts.pendingApproval;
  const filterOptions: FilterChipOption<KindFilter>[] = [
    {
      value: 'all',
      label: t('dashboard.actions.filter.all'),
      count: totalAll,
    },
    {
      value: 'entryBlocked',
      label: t(KIND_LABEL_KEYS.entryBlocked),
      count: safeCounts.entryBlocked,
    },
    {
      value: 'expiringPermit',
      label: t(KIND_LABEL_KEYS.expiringPermit),
      count: safeCounts.expiringPermit,
    },
    {
      value: 'pendingApproval',
      label: t(KIND_LABEL_KEYS.pendingApproval),
      count: safeCounts.pendingApproval,
    },
  ];

  const rows = query.data?.items ?? [];

  return (
    <section
      id="dashboard-actions"
      className="flex flex-col rounded-sm border border-border bg-surface shadow-sm"
    >
      <PanelHeader
        icon={ClipboardCheck}
        title={t('dashboard.actions.title')}
        right={
          <FilterChipGroup
            options={filterOptions}
            value={kind}
            onChange={(value) => {
              setKind(value);
              setPage(1);
            }}
            groupLabel={t('dashboard.actions.filter.groupLabel')}
            wrap={false}
          />
        }
      />

      {query.isPending ? (
        <LoadingRows />
      ) : query.isError ? (
        <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3 px-5 py-8">
          <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('dashboard.retry')}
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-8">
          {kind !== 'all' && totalAll > 0 ? (
            <EmptyState
              variant="panel"
              role="status"
              title={t('dashboard.actions.empty.filtered')}
              action={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setKind('all');
                    setPage(1);
                  }}
                >
                  {t('dashboard.actions.empty.showAll')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              variant="panel"
              title={t('dashboard.actions.empty')}
              description={t('dashboard.actions.empty.body')}
            />
          )}
        </div>
      ) : (
        <ul>
          {rows.map((item) => (
            <ActionItemRow
              key={`${item.kind}-${item.permitUuid}-${item.supplierUserUuid ?? ''}`}
              item={item}
            />
          ))}
        </ul>
      )}

      {query.data && rows.length > 0 ? (
        <div className="px-4 py-2">
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            filteredTotal={query.data.filteredTotal}
            onPageChange={setPage}
            compact
            labels={{
              nav: t('dashboard.actions.pagination.nav'),
              previous: t('dashboard.actions.pagination.previous'),
              next: t('dashboard.actions.pagination.next'),
            }}
          />
        </div>
      ) : null}
    </section>
  );
}
